/**
 * PicaOS Wallet Integration for Web3 Identity
 */

export interface PicaOSWallet {
  connect: () => Promise<string[]>;
  disconnect: () => Promise<void>;
  getAccounts: () => Promise<string[]>;
  signMessage: (message: string) => Promise<string>;
  isConnected: () => boolean;
  getNetwork: () => Promise<string>;
}

export interface WalletProvider {
  name: string;
  icon: string;
  connect: () => Promise<string>;
  disconnect: () => Promise<void>;
  isInstalled: () => boolean;
  getAddress: () => Promise<string>;
  signMessage: (message: string) => Promise<string>;
}

/**
 * Check if PicaOS wallet is available
 */
export const isPicaOSAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Check for PicaOS wallet injection
  return !!(
    (window as any).picaos ||
    (window as any).PicaOS ||
    (window as any).pica ||
    (window as any).ethereum?.isPicaOS
  );
};

/**
 * Get PicaOS wallet instance
 */
const getPicaOSWallet = (): PicaOSWallet | null => {
  if (typeof window === 'undefined') return null;
  
  // Try different possible injection points
  const wallet = (window as any).picaos || 
                 (window as any).PicaOS || 
                 (window as any).pica ||
                 ((window as any).ethereum?.isPicaOS ? (window as any).ethereum : null);
  
  return wallet;
};

/**
 * Connect to PicaOS wallet
 */
export const connectPicaOSWallet = async (): Promise<string> => {
  const wallet = getPicaOSWallet();
  
  if (!wallet) {
    throw new Error('PicaOS wallet not found. Please install PicaOS wallet extension.');
  }

  try {
    console.log('Connecting to PicaOS wallet...');
    
    // Try different connection methods
    let accounts: string[] = [];
    
    if (typeof wallet.connect === 'function') {
      accounts = await wallet.connect();
    } else if (typeof wallet.request === 'function') {
      accounts = await wallet.request({ method: 'eth_requestAccounts' });
    } else if (typeof wallet.enable === 'function') {
      accounts = await wallet.enable();
    } else {
      throw new Error('PicaOS wallet does not support connection');
    }

    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts found in PicaOS wallet');
    }

    const address = accounts[0];
    console.log('✓ Connected to PicaOS wallet:', address);
    
    return address;
  } catch (error: any) {
    console.error('PicaOS wallet connection failed:', error);
    throw new Error(`Failed to connect to PicaOS wallet: ${error.message}`);
  }
};

/**
 * Disconnect from PicaOS wallet
 */
export const disconnectPicaOSWallet = async (): Promise<void> => {
  const wallet = getPicaOSWallet();
  
  if (wallet && typeof wallet.disconnect === 'function') {
    try {
      await wallet.disconnect();
      console.log('✓ Disconnected from PicaOS wallet');
    } catch (error) {
      console.warn('Failed to disconnect from PicaOS wallet:', error);
    }
  }
};

/**
 * Sign a message with PicaOS wallet
 */
export const signMessageWithPicaOS = async (message: string, address: string): Promise<string> => {
  const wallet = getPicaOSWallet();
  
  if (!wallet) {
    throw new Error('PicaOS wallet not connected');
  }

  try {
    let signature: string;
    
    if (typeof wallet.signMessage === 'function') {
      signature = await wallet.signMessage(message);
    } else if (typeof wallet.request === 'function') {
      signature = await wallet.request({
        method: 'personal_sign',
        params: [message, address]
      });
    } else {
      throw new Error('PicaOS wallet does not support message signing');
    }

    return signature;
  } catch (error: any) {
    console.error('Message signing failed:', error);
    throw new Error(`Failed to sign message: ${error.message}`);
  }
};

/**
 * Get current PicaOS wallet address
 */
export const getPicaOSAddress = async (): Promise<string> => {
  const wallet = getPicaOSWallet();
  
  if (!wallet) {
    throw new Error('PicaOS wallet not connected');
  }

  try {
    let accounts: string[] = [];
    
    if (typeof wallet.getAccounts === 'function') {
      accounts = await wallet.getAccounts();
    } else if (typeof wallet.request === 'function') {
      accounts = await wallet.request({ method: 'eth_accounts' });
    } else {
      throw new Error('Cannot get accounts from PicaOS wallet');
    }

    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts found in PicaOS wallet');
    }

    return accounts[0];
  } catch (error: any) {
    console.error('Failed to get PicaOS address:', error);
    throw new Error(`Failed to get wallet address: ${error.message}`);
  }
};

/**
 * Validate Ethereum address format
 */
export const isValidEthereumAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Multi-wallet provider support
 */
export const getAvailableWalletProviders = (): WalletProvider[] => {
  const providers: WalletProvider[] = [];

  // PicaOS Wallet
  if (isPicaOSAvailable()) {
    providers.push({
      name: 'PicaOS',
      icon: '🎨',
      connect: connectPicaOSWallet,
      disconnect: disconnectPicaOSWallet,
      isInstalled: isPicaOSAvailable,
      getAddress: getPicaOSAddress,
      signMessage: (message: string) => {
        // This will be called with the connected address from the component
        throw new Error('Use signMessageWithAddress instead');
      }
    });
  }

  // MetaMask
  if (typeof window !== 'undefined' && (window as any).ethereum?.isMetaMask) {
    providers.push({
      name: 'MetaMask',
      icon: '🦊',
      connect: async () => {
        const accounts = await (window as any).ethereum.request({ 
          method: 'eth_requestAccounts' 
        });
        if (!accounts || accounts.length === 0) {
          throw new Error('No accounts found in MetaMask wallet');
        }
        return accounts[0];
      },
      disconnect: async () => {
        // MetaMask doesn't have a disconnect method
      },
      isInstalled: () => !!(window as any).ethereum?.isMetaMask,
      getAddress: async () => {
        const accounts = await (window as any).ethereum.request({ 
          method: 'eth_accounts' 
        });
        if (!accounts || accounts.length === 0) {
          throw new Error('No accounts found in MetaMask wallet. Please connect your wallet first.');
        }
        return accounts[0];
      },
      signMessage: (message: string) => {
        // This will be called with the connected address from the component
        throw new Error('Use signMessageWithAddress instead');
      }
    });
  }

  // For demo purposes, add a mock wallet provider if no real wallets are available
  if (providers.length === 0) {
    providers.push({
      name: 'Demo Wallet',
      icon: '🔑',
      connect: async () => {
        // Return a mock Ethereum address
        return '0x742d35Cc6634C0532925a3b844Bc454e4438f44e';
      },
      disconnect: async () => {
        console.log('Disconnected from Demo Wallet');
      },
      isInstalled: () => true,
      getAddress: async () => {
        return '0x742d35Cc6634C0532925a3b844Bc454e4438f44e';
      },
      signMessage: async (message: string) => {
        // Return a mock signature
        return '0x' + Array(130).fill('0').join('');
      }
    });
  }

  return providers;
};

/**
 * Sign message with specific wallet provider and address
 */
export const signMessageWithProvider = async (
  provider: WalletProvider, 
  message: string, 
  address: string
): Promise<string> => {
  if (provider.name === 'PicaOS') {
    return signMessageWithPicaOS(message, address);
  } else if (provider.name === 'MetaMask') {
    return await (window as any).ethereum.request({
      method: 'personal_sign',
      params: [message, address]
    });
  } else if (provider.name === 'Demo Wallet') {
    // Return a mock signature for demo wallet
    return '0x' + Array(130).fill('0').join('');
  } else {
    throw new Error(`Unsupported wallet provider: ${provider.name}`);
  }
};

/**
 * Create Web3 identity verification message
 */
export const createIdentityVerificationMessage = (address: string, timestamp: number): string => {
  return `Aegis Vault Identity Verification

Address: ${address}
Timestamp: ${timestamp}
Action: Verify Web3 Identity

By signing this message, you confirm your ownership of this wallet address for use with Aegis Vault's Will & Witness feature.

This signature will not trigger any blockchain transactions or cost any gas fees.`;
};

/**
 * Verify Web3 identity by signing a message with a connected address
 */
export const verifyWeb3Identity = async (
  provider: WalletProvider, 
  connectedAddress: string
): Promise<{
  address: string;
  signature: string;
  timestamp: number;
  verified: boolean;
}> => {
  try {
    if (!connectedAddress) {
      throw new Error('No connected address provided');
    }
    
    // For demo wallet, accept any address format
    if (provider.name === 'Demo Wallet') {
      const timestamp = Date.now();
      const message = createIdentityVerificationMessage(connectedAddress, timestamp);
      const signature = await provider.signMessage(message);
      
      return {
        address: connectedAddress,
        signature,
        timestamp,
        verified: true
      };
    }
    
    // For real wallets, validate Ethereum address format
    if (!isValidEthereumAddress(connectedAddress)) {
      throw new Error('Invalid parameters: must provide a valid Ethereum address.');
    }
    
    const timestamp = Date.now();
    const message = createIdentityVerificationMessage(connectedAddress, timestamp);
    
    const signature = await signMessageWithProvider(provider, message, connectedAddress);
    
    // In a production environment, you would verify the signature on the backend
    // For now, we'll assume it's valid if we got a signature
    const verified = !!signature;
    
    return {
      address: connectedAddress,
      signature,
      timestamp,
      verified
    };
  } catch (error: any) {
    console.error('Web3 identity verification failed:', error);
    throw new Error(`Identity verification failed: ${error.message}`);
  }
};